import { ReactNode, useMemo, ElementType } from 'react';
import type CSS from 'csstype';
import classnames from 'classnames';
import useStyles from './Typography.styles';

const headers = ['h1', 'h2', 'h3', 'h4', 'h5'] as const;
const paragraphs = [
  'accessibility',
  'paragraph',
  'small-text',
  'tiny-text',
  'xtra-small-text',
] as const;

const variants = [...headers, ...paragraphs];

type TVariant = (typeof variants)[number];
type TComponent = TVariant | keyof JSX.IntrinsicElements;

const getComponent = (val: TComponent) =>
  paragraphs.includes(val as (typeof paragraphs)[number]) ? 'p' : val;

export interface ITypographyProps {
  children: ReactNode;
  /** Custom class name passed to the root node */
  className?: string;
  /** The component used for the root node */
  component?: TComponent;
  /** For block or inline-block elements.
   * If truthy text will not wrap, it will truncate and add an ellipsis
   */
  noWrap?: boolean;
  /** Set the text-align styling property for the component */
  textAlign?: CSS.Properties['textAlign'];
  /** Applies the theme typography styles */
  variant?: TVariant;
}

const Typography = ({
  children,
  className,
  component,
  noWrap = false,
  textAlign = 'inherit',
  variant = 'paragraph',
}: ITypographyProps) => {
  const classes = useStyles(useMemo(() => ({ textAlign }), [textAlign]));
  const Component = getComponent(component || variant) as ElementType;

  return (
    <Component
      className={classnames(classes.common, classes[variant], className, {
        [classes.noWrap]: noWrap,
      })}
      data-qa="typography-component"
    >
      {children}
    </Component>
  );
};

export default Typography;
